import fieldMapping from './field-mapping.json';
import {
  ENTITY_CORPORATE,
  ENTITY_LIMITED_LIABILITY,
  ENTITY_PARTNERSHIP,
  ENTITY_SOLE_PROPRIETORSHIP,
  ENTITY_TRUST,
} from '../../components/AccountType/accountTypes';

const ENTITY_TYPE_OPTIONS = {
  [ENTITY_CORPORATE]: '10002',
  [ENTITY_LIMITED_LIABILITY]: '10003',
  [ENTITY_PARTNERSHIP]: '10004',
  [ENTITY_SOLE_PROPRIETORSHIP]: '10005',
  [ENTITY_TRUST]: '10006',
};

const ASSET_CLASS_OPTIONS = {
  equities: '1',
  futures: '2',
  futuresoptions: '3',
};

// Own-property test for the id-keyed lookup tables below. Bare `table[key]` reaches
// Object.prototype, where every name resolves to a truthy function.
function has(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

// Drops keys whose value is absent so the payload carries only values we hold.
// Compliance's "if available" wording requires a missing value to become a blank,
// never an error and never an empty string written over existing form data.
function compact(object) {
  return Object.keys(object).reduce((accumulator, key) => {
    const value = object[key];
    if (value !== undefined && value !== null && value !== '') {
      return { ...accumulator, [key]: value };
    }
    return accumulator;
  }, {});
}

// Formstack's prefill API rejects the whole request when a scalar arrives as a JSON number
// rather than a string, and one bad field fails every other field with it. The proxy surfaces
// that as a bare 500 naming no field, so it is expensive to diagnose and cheap to prevent.
// Verified against the live API 2026-09-29: field 196107628 sent as 1369441885 returns 500 and
// '1369441885' returns 200, with the rest of the payload byte-identical. That field, the Fdcnid,
// is now Ellis's to send (T071), but values still arrive here straight from the ellis API, which
// can return numbers, so the coercion belongs at this boundary rather than in each resolver, where
// a future numeric field would have to remember to repeat it.
function stringifyScalars(valueDto) {
  return Object.keys(valueDto).reduce((accumulator, key) => {
    const value = valueDto[key];

    // The only array is a checkbox `subvalues` list of `{ subvalue }` objects.
    if (Array.isArray(value)) {
      return {
        ...accumulator,
        [key]: value.map(entry => (entry !== null && typeof entry === 'object'
          ? stringifyScalars(entry)
          : String(entry))),
      };
    }

    return { ...accumulator, [key]: String(value) };
  }, {});
}

function getPrimaryContact(state) {
  const { contact } = state;
  if (Array.isArray(contact)) {
    return contact.find(c => c.type === 'primary') || {};
  }
  return contact || {};
}

// The sales rep and promo code are written to the contact record by
// AccountTypeFormContainer and to the registration record at sign-up, and
// neither page writes the other. The contact therefore holds the later choice
// and wins; registration is the fallback for applications where the value was
// only ever captured at sign-up. Preferring registration would send a stale rep
// to CRM whenever the applicant changed it on /account-type.
function resolveContactFirst(state, key) {
  const contactValue = getPrimaryContact(state)[key];
  if (contactValue !== undefined && contactValue !== null && contactValue !== '') {
    return contactValue;
  }
  return state.registration && state.registration[key];
}

// Returns the address belonging to the primary contact, or {} when none does.
// Deliberately has no `|| address[0]` fallback: in a multi-holder entity that
// fallback silently sends a different person's address to a third party.
function getPrimaryAddress(state, primaryContact) {
  const { address } = state;
  if (!address || primaryContact.id === undefined || primaryContact.id === null) return {};

  const addresses = Array.isArray(address) ? address : [address];
  const match = addresses.find(a => a.contactId === primaryContact.id);
  if (match) return match;

  // A lone address object with no owner recorded cannot belong to another
  // holder, so it is the primary contact's by construction.
  if (addresses.length === 1 && addresses[0].contactId === undefined) return addresses[0];

  return {};
}

// Turns an ISO country code into the country name. Every country-bearing field on the live
// form — the standalone Country of Registration select and the `country` subfield of an address
// — offers full names as its option values, so a raw code matches no option. Formstack fails
// silently on an unmatched option, so the symptom is a blank field rather than an error.
// Returns undefined when the code cannot be resolved, which `compact` then drops: omitting the
// field is preferable to writing a value the form will discard anyway.
function resolveCountryName(state, code) {
  if (!code) return undefined;
  const country = (state.countries || []).find(c => c.countryCode === code);
  return country ? country.country : undefined;
}

// Gates declared by `condition` in field-mapping.json. A field whose condition
// is unknown here is never emitted — an unrecognised gate must fail closed,
// because the failure mode is transmitting PII that nobody authorised.
const conditions = {
  existingCustomer: state => !!(state.registration && state.registration.isExistingCustomer),
};

// Resolves a value from the Redux store, keyed by the `formstackFieldLabelKey`
// declared in field-mapping.json. Each returns a Formstack value DTO, or null
// when nothing is available to send.
//
// The form's four hidden fields — Application ID, Fdcnid, Environment and AOP URL — have no
// resolver and no mapping entry: Ellis sets them at prefill from its own records and config
// (T071, 2026-10-22). Their mapping is recorded in ellis
// src/lib/services/formstack/field-mapping.reference.json.
const resolvers = {
  // The live form's Name field exposes only `first` and `last` subfields — the middle-name
  // subfield is not enabled on it (verified against the live form 2026-09-29). A middle initial
  // sent here would be discarded silently, so it is not sent and not claimed in the mapping.
  // If CRM Ops enables the subfield, re-verify its key before adding it back: Formstack's name
  // subfield is `middle`, not `initial`.
  primary_authorized_name: (state) => {
    const contact = getPrimaryContact(state);
    return compact({
      first: contact.firstName,
      last: contact.lastName,
    });
  },

  country_of_registration: state =>
    ({ value: resolveCountryName(state, getPrimaryContact(state).countryOfResidence) }),

  // Compliance authorised this for existing customers only, and only when available; the
  // `existingCustomer` gate above enforces that. Named for the representative's HOME address,
  // which is what is authorised — not the entity's `principal_business_address` (194087744),
  // the field this was once parked against and must never be sent to.
  primary_authorized_home_address: (state) => {
    const address = getPrimaryAddress(state, getPrimaryContact(state));
    // `state` is the Formstack subfield key for a region; `region` is not part
    // of the Address DTO and would be discarded without error. `address2` is likewise
    // omitted: Address Line 2 is not enabled on the live form (verified 2026-09-29), so it
    // has no subfield to land in. The `country` subfield is a dropdown of full country
    // names, so it takes the resolved name rather than the stored ISO code.
    return compact({
      address: address.residentialAddress1,
      city: address.residentialCity,
      country: resolveCountryName(state, address.residentialCountry),
      state: address.residentialRegion,
      zip: address.residentialZip,
    });
  },

  entity_email_address: state => ({ value: getPrimaryContact(state).email }),

  entity_phone_number: state => ({ value: getPrimaryContact(state).phone }),

  sales_representative: (state) => {
    const repKey = resolveContactFirst(state, 'hearAboutUsSalesRep');
    const rep = (state.salesReps || []).find(salesRep => salesRep.key === repKey);
    return { value: rep && rep.title };
  },

  promo_code: state => ({ value: resolveContactFirst(state, 'promoCode') }),

  entity_type: state => ({
    value: ENTITY_TYPE_OPTIONS[state.accountType && state.accountType.accountType],
  }),

  account_type: state => ({
    subvalues: ((state.accountType && state.accountType.assetTypes) || [])
      .map(assetType => ASSET_CLASS_OPTIONS[assetType])
      .filter(option => !!option)
      .map(subvalue => ({ subvalue })),
  }),
};

function isEmpty(valueDto) {
  if (!valueDto) return true;
  if (Array.isArray(valueDto.subvalues)) return valueDto.subvalues.length === 0;
  return Object.keys(compact(valueDto)).length === 0;
}

// Builds the `fields` payload for Formstack's prefill API: an array of
// `{ id, value }` entries, one per mapped Formstack field that both resolves to
// a value and passes its declared condition. Fields with nothing to send are
// omitted rather than sent blank.
export default function buildPrefillFieldValues(state) {
  if (!state) return [];

  return fieldMapping.fields.reduce((fields, entry) => {
    const { formstackFieldId, formstackFieldLabelKey, condition } = entry;
    // An entry with no confirmed id is deferred: withholding the id in the
    // mapping is what switches a field off, so the audit record and the runtime
    // behaviour can never disagree. Nothing is deferred today (B2 and B9 are
    // both closed); the branch stays because that is the switch.
    if (!formstackFieldId) return fields;

    if (condition) {
      // Own-property guard, not decoration: `conditions[condition]` walks the prototype chain, so
      // `condition: "constructor"` would resolve to Object — truthy, and Object(state) is truthy
      // too — flipping this gate from fail-closed to fail-OPEN and transmitting the gated home
      // address for every applicant. field-mapping.json is edited on input from Compliance and
      // CRM Ops, not by people reasoning about Object.prototype, and ESLint cannot catch it here
      // because .eslintrc disables security/detect-object-injection (T042).
      const isAllowed = has(conditions, condition) ? conditions[condition] : null;
      if (!isAllowed || !isAllowed(state)) return fields;
    }

    // Same guard, larger blast radius: `formstackFieldLabelKey: "constructor"` would resolve to
    // Object, and Object(state) returns the Redux store itself — emitting every slice, including
    // authToken and the SSN/DOB/financial data this payload is designed to exclude.
    const resolver = has(resolvers, formstackFieldLabelKey)
      ? resolvers[formstackFieldLabelKey]
      : null;
    if (!resolver) return fields;

    const value = resolver(state);
    if (isEmpty(value)) return fields;

    return [...fields, { id: formstackFieldId, value: stringifyScalars(compact(value)) }];
  }, []);
}
